import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';

import { useNotification, useBaseHistory, useModuleRoute } from 'spindl-shared-components';

import { NOTIFICATION_DURATION } from '../../../lib/constants';
import useGetAccount from '../../../modules/auth/useGetAccount';

import { getUsersListUseCase, sendInvitationEmailsUseCase } from './InviteUsers.interactor';
import { InviteUsersCombinedProps, ChipRecord } from './types';
import { getTranslation } from '../../../lib/reactUtils';
import { isEmail } from '../../../lib/utils';

const usePresenter = (props: InviteUsersCombinedProps): InviteUsersCombinedProps => {
  const { t } = useTranslation();
  const history = useBaseHistory();
  const [emailList, setEmailList] = useState<string[]>([]);
  const [validChipCount, setValidChipCount] = useState<number>(0);
  const { trigger } = useNotification();
  const { account } = useGetAccount();

  const { communicationUrl } = useModuleRoute();

  const { mutateAsync: sendInvites } = useMutation(sendInvitationEmailsUseCase);
  const { data: users } = useQuery(['getUsers'], () => getUsersListUseCase());

  const handleBackToUserList = () => {
    history.push('/');
  };

  const handleBackButton = () => {
    history.replace('/wizard');
  };

  const triggerInvitationSentNotification = () => {
    if (emailList.length) {
      const message = emailList.length === 1
        ? getTranslation('invite_users.single_invite_notification', {
          invitationEmail: emailList[0],
        })
        : getTranslation('invite_users.multiple_invite_notification', {
          totalInvitationEmails: emailList.length,
        });
      trigger({
        duration: NOTIFICATION_DURATION,
        message,
        type: 'Positive',
      });
    }
  };

  const handleSendInviteEmails = async () => {
    if (emailList.length && account) {
      // TODO: getting role from role endpoint
      await sendInvites({
        senderFirstName: account.firstName,
        senderLastName: account.lastName,
        senderEmail: account.email,
        emails: emailList,
      });
      triggerInvitationSentNotification();
      history.push('/');
    }
  };

  const updateEmailList = (chipRecords: ChipRecord[]) => {
    const emailValues: string[] = [];
    chipRecords.forEach(chipRecord => {
      emailValues.push(chipRecord.value);
    });
    setEmailList(emailValues.filter((email, pos) => emailValues.indexOf(email) === pos));
  };

  const updateValidChipCount = (count: number) => {
    setValidChipCount(count);
  };

  const invalidChipCount = emailList.length - validChipCount;

  const validateChip = (chip: string): boolean => {
    if (users) {
      return isEmail(chip) && !users.some(user => user.email === chip);
    }
    return true;
  };
  
  return {
    ...props,
    button: {
      onClick: handleBackToUserList,
    },
    text: {
      value: t('invite_users.header'),
    },
    text1: {
      value: t('invite_users.text'),
    },
    infoText: {
      value: t('invite_users.text1'),
    },
    chip: {
      // TODO: change value to default role when endpoint is ready
      text: {
        value: t('place_holder.role'),
      },
    },
    chipField: {
      label: {
        value: t('column.name.email'),
      },
      allowNew: true,
      multiSelect: true,
      open: false,
      setSelectedValue: updateEmailList,
      setValidChipCount: updateValidChipCount,
      validateChip,
      text: {
        value: t('invite_users.invalid_email', {
          invalidChipCount: invalidChipCount,
          plural: (invalidChipCount > 1) ? 'es' : '',
          plural2: (invalidChipCount > 1) ? '' : 's',
        }),
      },
      button: {
        text: {
          value: t('button.remove_invalid'),
        },
      },
    },
    reviewUsersText: 
      <Trans key='invite_users.review_users_text'>
        <Link to={`${communicationUrl}/editor/sign-up-invite?view=preview`} target='_blank'>
          {t('invite_users.review_users_text.tag')}
        </Link>
        {t('invite_users.review_users_text.content')} 
      </Trans>,
    backButton: {
      text: {
        value: t('button.back'),
      },
      onClick: handleBackButton,
    },
    sendButton: {
      text: {
        value: t('button.send_invite_emails', {
          validChipCount: validChipCount,
          plural: (validChipCount > 1) ? 's' : '',
        }),
      },
      onClick: handleSendInviteEmails,
      disabled: emailList.length !== validChipCount || emailList.length === 0,
    },
  };
};

export default usePresenter;
